import {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  ReactNode,
} from 'react';
import {
  Autocomplete,
  AutocompleteItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
  Button,
  Link as NextUILink,
  LinkProps as NextUILinkProps,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  useDisclosure,
  User,
} from '@nextui-org/react';
import { useAsyncList } from '@react-stately/data';
import { useDebounce } from 'use-debounce';

import {
  Outlet,
  Link as ReactRouterLink,
  LinkProps as ReactRouterLinkProps,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import * as Sentry from '@sentry/react';

import { SendIcon } from '../icons';

import { CosmoClient, cosmoClient, Profile } from '../cosmo';
import { useAccounts, useCurrentAccount, useProfile } from '../credentials';

import { getRamperWalletSecrets } from '../ramper';
import { PolarisClient } from '../polaris';

export function Link(props: NextUILinkProps & ReactRouterLinkProps) {
  return <NextUILink as={ReactRouterLink} {...props} />;
}

function CosmoNicknameAutocomplete({
  nickname,
  setNickname,
  isInvalid = false,
  errorMessage,
}: {
  nickname: string;
  setNickname: Dispatch<SetStateAction<string>>;
  isInvalid?: boolean;
  errorMessage?: ReactNode;
}) {
  let list = useAsyncList<Profile>({
    async load({ filterText }) {
      if (!filterText || filterText.length < 4) {
        return { items: [] };
      }
      if (!cosmoClient.credentials) {
        let response = await fetch(
          `https://cache.nova.gd/user/v1/search?query=${encodeURIComponent(
            filterText,
          )}`,
        );
        let { results: items } = await response.json();
        return { items };
      } else {
        let { results: items } = await cosmoClient.searchUsers(filterText);
        return {
          items,
        };
      }
    },
  });
  let [listFilterText] = useDebounce(nickname, 100);
  useEffect(() => list.setFilterText(listFilterText), [listFilterText]);
  return (
    <Autocomplete
      isClearable
      aria-label='Search user by COSMO ID'
      placeholder='Search user by COSMO ID'
      allowsCustomValue
      isLoading={list.isLoading}
      items={list.items}
      inputValue={nickname}
      onInputChange={setNickname}
      inputProps={{
        classNames: {
          input: 'text-base placeholder:text-small',
        },
      }}
      className='min-w-72'
      popoverProps={{ placement: 'top' }}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
    >
      {list.items.map(item => (
        <AutocompleteItem key={item.nickname} textValue={item.nickname}>
          <User
            name={item.nickname}
            description={item.address}
            avatarProps={{
              src: 'https://static.cosmo.fans/uploads/images/img_profile_gallag@3x.png',
            }}
          />
        </AutocompleteItem>
      ))}
    </Autocomplete>
  );
}

function LoginModal({ onClose }: { onClose: () => void }) {
  let { addAccount } = useAccounts();

  let [screen, setScreen] = useState(0);
  let [email, setEmail] = useState('');
  let [transactionId, setTransactionId] = useState('');
  let [pendingToken, setPendingToken] = useState('');
  let [isLoading, setIsLoading] = useState(false);
  let [error, setError] = useState('');

  async function onNextButtonPress() {
    try {
      setIsLoading(true);
      setError('');
      if (screen === 0) {
        let { transactionId, pendingToken } =
          await cosmoClient.requestSignIn(email);
        setTransactionId(transactionId);
        setPendingToken(pendingToken);
        setScreen(1);
      } else if (screen === 1) {
        let { customToken, socialLoginUserId, ...credentials } =
          await cosmoClient.signIn(email, transactionId, pendingToken);
        let ramperWalletSecrets = await getRamperWalletSecrets(
          socialLoginUserId,
          customToken,
        );
        let profile: Profile;
        try {
          profile = await new PolarisClient().signIn(socialLoginUserId);
        } catch (error) {
          console.error(error);
          let temporaryCosmoClient = new CosmoClient();
          temporaryCosmoClient.credentials = credentials;
          profile = await temporaryCosmoClient.getProfile();
        }
        addAccount({
          credentials,
          profile,
          ramperWalletSecrets,
          socialLoginUserId,
        });
        setScreen(2);
      } else if (screen === 2) {
        onClose();
      }
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      if (error instanceof Error) {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        onNextButtonPress();
      }}
    >
      <ModalHeader>Login to COSMO</ModalHeader>
      <ModalBody>
        {screen === 0 && (
          <>
            <p>Please enter your email address to request a sign in link.</p>
            <Input
              type='email'
              label='Email'
              placeholder='Enter your email'
              value={email}
              onValueChange={setEmail}
              classNames={{ input: 'text-base placeholder:text-small' }}
            />
          </>
        )}
        {screen === 1 && (
          <>
            <p>
              We have sent you an email with a link to confirm your sign in.
              Please check your inbox and click on the link{' '}
              <span className='text-blue-500'>
                confirm from a different device
              </span>
              .
            </p>
            <img
              src={
                new URL('../resources/cosmo-login-mail.png', import.meta.url)
                  .href
              }
            />
          </>
        )}
        {screen === 2 && <p>You have successfully signed in.</p>}
        {error && <p className='text-red-500'>{error}</p>}
      </ModalBody>
      <ModalFooter>
        <Button color='danger' variant='light' onPress={onClose}>
          Close
        </Button>
        <Button color='primary' type='submit' isLoading={isLoading}>
          {screen === 0 && 'Send Email'}
          {screen === 1 && 'Check Confirmation'}
          {screen === 2 && 'Dismiss'}
        </Button>
      </ModalFooter>
    </form>
  );
}

export default function Root() {
  let { accounts, removeAccount, setCurrentAccount } = useAccounts();
  let account = useCurrentAccount();
  let profile = useProfile();
  let { isOpen, onOpen, onOpenChange } = useDisclosure();
  let [isMenuOpen, setIsMenuOpen] = useState(false);
  let location = useLocation();
  let navigate = useNavigate();
  let [nickname, setNickname] = useState('');
  useEffect(() => setIsMenuOpen(false), [location]);
  return (
    <>
      <Navbar isMenuOpen={isMenuOpen} onMenuOpenChange={setIsMenuOpen}>
        <NavbarContent>
          <NavbarMenuToggle
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            className='sm:hidden'
          />
          <NavbarBrand className='grow-0'>
            <div>
              <p className='font-bold text-inherit'>
                <Link color='foreground' to='/'>
                  Polaris
                </Link>
              </p>
              <p className='text-tiny leading-[.5rem] text-foreground-500'>
                by <a href='https://twitter.com/3somsoc'>@3somsoc</a>
              </p>
            </div>
          </NavbarBrand>
          <NavbarItem
            className='hidden sm:block'
            isActive={location.pathname === '/'}
          >
            <Link color='foreground' to='/'>
              Objekts
            </Link>
          </NavbarItem>
          <NavbarItem
            className='hidden sm:block'
            isActive={location.pathname === '/gravity'}
          >
            <Link to='/gravity'>Gravity Stats</Link>
          </NavbarItem>
        </NavbarContent>
        <NavbarMenu>
          <NavbarMenuItem>
            <Link color='foreground' to='/'>
              Objekts
            </Link>
          </NavbarMenuItem>
          <NavbarMenuItem>
            <Link color='foreground' to='/gravity'>
              Gravity Stats
            </Link>
          </NavbarMenuItem>
          <NavbarMenuItem>
            <form
              className='flex gap-2'
              onSubmit={e => {
                e.preventDefault();
                navigate(`/@${nickname}`);
              }}
            >
              <CosmoNicknameAutocomplete
                nickname={nickname}
                setNickname={setNickname}
              />
              <Button type='submit' isIconOnly color='primary' variant='flat'>
                <SendIcon className='w-6 h-6' />
              </Button>
            </form>
          </NavbarMenuItem>
        </NavbarMenu>
        <NavbarContent justify='end'>
          <NavbarItem className='hidden sm:block'>
            <form
              className='flex gap-2'
              onSubmit={e => {
                e.preventDefault();
                navigate(`/@${nickname}`);
              }}
            >
              <CosmoNicknameAutocomplete
                nickname={nickname}
                setNickname={setNickname}
              />
              <Button type='submit' isIconOnly color='primary' variant='flat'>
                <SendIcon className='w-6 h-6' />
              </Button>
            </form>
          </NavbarItem>
          <NavbarItem>
            {profile ? (
              <Dropdown shouldBlockScroll={false} triggerScaleOnOpen={false}>
                <DropdownTrigger>
                  <Button
                    radius='full'
                    variant='light'
                    size='lg'
                    className='ps-1 pe-4'
                  >
                    <User
                      name={profile.nickname}
                      description={
                        !account?.socialLoginUserId
                          ? '⚠ Please relogin! '
                          : profile.email
                      }
                      avatarProps={{
                        src:
                          profile.profileImageUrl ||
                          'https://static.cosmo.fans/uploads/images/img_profile_gallag@3x.png',
                      }}
                      classNames={{
                        description: !account?.socialLoginUserId
                          ? 'text-warning'
                          : '',
                      }}
                    />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  onAction={key => {
                    if (key === 'login') {
                      onOpen();
                    } else if (key === 'logout') {
                      removeAccount(profile!.id);
                    } else if (
                      typeof key === 'string' &&
                      key.startsWith('profile-')
                    ) {
                      let profileId = parseInt(key.slice('profile-'.length));
                      setCurrentAccount(profileId);
                    }
                  }}
                  aria-label='Account Actions'
                >
                  {[
                    ...accounts.map(account => (
                      <DropdownItem
                        key={`profile-${account.profile.id}`}
                        className={
                          !account?.socialLoginUserId ? 'text-warning' : ''
                        }
                      >
                        @{account.profile.nickname}
                        {!account?.socialLoginUserId ? ' (relogin needed)' : ''}
                      </DropdownItem>
                    )),
                    <DropdownItem key='login'>Add account</DropdownItem>,
                    <DropdownItem
                      key='logout'
                      className='text-danger'
                      color='danger'
                    >
                      Logout
                    </DropdownItem>,
                  ]}
                </DropdownMenu>
              </Dropdown>
            ) : (
              <Button color='primary' onClick={onOpen}>
                Login
              </Button>
            )}
          </NavbarItem>
        </NavbarContent>
      </Navbar>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} placement='top-center'>
        <ModalContent>
          {onClose => <LoginModal onClose={onClose} />}
        </ModalContent>
      </Modal>
      <Outlet />
    </>
  );
}
